import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking | object = logged in | false = not logged in
    const [cartCount, setCartCount] = useState(0);

    const refreshCart = useCallback(async () => {
        try {
            const { data } = await api.get("/cart");
            setCartCount(data.items.reduce((s, i) => s + i.quantity, 0));
        } catch {
            setCartCount(0);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                setUser(data);
                if (data.role === "admin" || data.status === "approved") refreshCart();
            } catch {
                setUser(false);
                setAuthToken(null);
            }
        })();
    }, [refreshCart]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.token) setAuthToken(data.token);
            setUser(data);
            if (data.role === "admin" || data.status === "approved") await refreshCart();
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            if (data.token) setAuthToken(data.token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        setAuthToken(null);
        setUser(false);
        setCartCount(0);
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout, cartCount, refreshCart }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
